import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import NavLink from './components/NavLink';
import {ROUTES} from 'common/constants';
import useUserStore from 'common/stores/user';

import {
	Layout,
	Drawer,
	// SettingsIcon,
	ProfileIcon,
} from '@phonectas/unifon-ui-kit';
// import BackofficeIcon from 'common/components/icons/BackOfficeIcon';
import PermissionManager from 'common/components/permissions/PermissionManager';
import ThreeSixtyIcon from 'common/components/icons/ThreeSixtyIcon';
import MrcIcon from 'common/components/icons/MrcIcon';
import BmoNavIcon from 'common/components/icons/BmoNavIcon';

interface MainNavProps {
	expand: boolean;
}

/**
 * @param {boolean} expand
 * @return {JSX.Element}
 * @constructor
 */

const MainNav: React.FC<MainNavProps> = ({expand}) => {
	const [t] = useTranslation();
	const user = useUserStore((state) => state.user);

	// Same rules as route access (USER_ACCESS), so the menu never hides or shows a section wrongly
	const permissionManager = useMemo(() => user ? new PermissionManager(user) : null, [user]);
	const canOpen = (route: string) => permissionManager?.hasAccess(route) ?? false;
	const hasBmo = canOpen(ROUTES.BMODASHBOARD);
	const hasThreeSixty = canOpen(ROUTES.THREE_SIXTY);
	const hasMrc = canOpen(ROUTES.MRC);

	const routes = useMemo(() => {
		const routesArray = [
			{
				to: ROUTES.PROFILE,
				Icon: ProfileIcon,
				text: t('Profile'),
				size: 'xl',
			},
			...(hasBmo ? [{
				to: ROUTES.BMODASHBOARD,
				Icon: BmoNavIcon,
				text: t('BMO'),
				size: 'xl',
			}] : []),
			...(hasThreeSixty ? [{
				to: ROUTES.THREE_SIXTY,
				Icon: ThreeSixtyIcon,
				text: t('Unifon 360'),
				size: 'xl',
			}] : []),
			...(hasMrc ? [{
				to: ROUTES.MRC,
				Icon: MrcIcon,
				text: t('MRC'),
				size: 'xl',
			}] : []),
		];

		return routesArray;
	}, [t, hasBmo, hasThreeSixty, hasMrc]);

	return (
		<Drawer
			expand={expand}
			variant="permanent"
			finalSize={225}
			initialSize={69}
		>
			<Layout direction="column" gap="none" fullWidth>
				{routes.map(({to, Icon, text, size}) => (
					<NavLink to={to} key={to} Icon={Icon} text={text} size={size} expand={expand} />
				))}
			</Layout>
		</Drawer>
	);
}

export default MainNav;
